import { OpportunityForm, Stage, Opportunity } from './OpportunityPageTypes';
import i18n from '../i18n';


/* ───────────────────────────────
   FUNÇÕES UTILITÁRIAS
──────────────────────────────── */

export type OpportunityListRow = Opportunity & {
  owner_name: string;
  contact_name: string;
  current_stage: string;
  total_duration: number;
};

export function buildOpportunityNameMaps(
  ownerOptions: { value: string; label: string }[],
  contactOptions: { value: string; label: string }[],
) {
  const ownerNameById: Record<string, string> = {};
  for (const owner of ownerOptions) {
    ownerNameById[owner.value] = owner.label;
  }
  const contactNameById: Record<string, string> = {};
  for (const contact of contactOptions) {
    contactNameById[contact.value] = contact.label;
  }
  return { ownerNameById, contactNameById };
}

export function formatOpportunityListRow(
  opportunity: Opportunity,
  ownerNameById: Record<string, string>,
  contactNameById: Record<string, string>,
): OpportunityListRow {
  const sortedStages = [...opportunity.stages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const currentStage =
    opportunity.stages.find((stage) => stage.finished_at === null) || sortedStages[0];

  return {
    ...opportunity,
    owner_name: opportunity.owner ? ownerNameById[opportunity.owner] ?? '' : '',
    contact_name: opportunity.contact_id ? contactNameById[opportunity.contact_id] ?? '' : '',
    current_stage: currentStage?.name ?? '',
    total_duration: calculateTotalDuration(sortedStages),
  };
}

export function calculateTotalDuration(stages: Stage[]): number {
  if (stages.length === 0) return 0;
  const firstDate = new Date(stages[0].created_at);
  const lastStage = stages[stages.length - 1];
  const lastDate = new Date(lastStage.finished_at || new Date());
  const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}


/** Active stage: matches table view (unfinished stage, else earliest by created_at). */
export function getCurrentStageForOpportunity(opportunity: Opportunity): Stage {
  const sortedStages = [...opportunity.stages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  return (
    opportunity.stages.find((stage) => stage.finished_at === null) ||
    sortedStages[0]
  );
}

export function calculateStageDuration(stage: Stage): number {
  const start = new Date(stage.created_at);
  const end = stage.finished_at ? new Date(stage.finished_at) : new Date();
  const diff = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function handleStageChange(
  opportunity: Opportunity,
  newStage: 'Value Proposition' | 'Quotation' | 'Demonstration' | 'Negociation' | 'Closed',
  onUpdate: (updated: OpportunityForm, id: string) => void
) {
  const nowDate = new Date().toISOString().split('T')[0];
  const currentStage = opportunity.stages[opportunity.stages.length - 1];

  if (currentStage.name === 'Closed') {
    alert(i18n.t('opportunity.alerts.alreadyClosing'));
    return;
  }

  if (opportunity.stages.some(stage => stage.name === newStage)) {
    alert(i18n.t('opportunity.alerts.stageAlreadyPassed', { stage: newStage }));
    return;
  }

  const updatedStages = opportunity.stages.map((st, idx) => {
    if (idx === opportunity.stages.length - 1 && !st.finished_at) {
      return { ...st, finished_at: nowDate };
    }
    return st;
  });
  updatedStages.push({
    id: `s-${opportunity.id}-${Date.now()}`,
    opportunity_id: opportunity.id,
    name: newStage,
    finished_at: undefined,
    created_at: nowDate
  });
  const updatedOpportunity = { ...opportunity, stages: updatedStages };
  onUpdate(updatedOpportunity, opportunity.id);
}